/** Serialize a stateful data source, coalesce identical work, and skip superseded
 * queued requests. Only the latest caller may publish a result. */
export function createLatestRequest<T>() {
  interface Request {
    key: string;
    promise: Promise<T | undefined>;
  }
  let latest: Request | null = null;
  let running: Request | null = null;
  let tail: Promise<unknown> = Promise.resolve();
  return {
    run(key: string, load: () => Promise<T>): Promise<T | undefined> {
      if (latest?.key === key) return latest.promise;
      // A queued selection must not hide reusable work already in flight.
      if (running?.key === key) {
        latest = running;
        return running.promise;
      }
      const job: Request = {
        key,
        promise: Promise.resolve<T | undefined>(undefined),
      };
      latest = job;
      job.promise = tail
        .catch(() => undefined)
        .then(async () => {
          if (latest !== job) return undefined;
          running = job;
          try {
            const value = await load();
            return latest === job ? value : undefined;
          } catch (error) {
            if (latest === job) throw error;
            return undefined;
          } finally {
            running = null;
            if (latest === job) latest = null;
          }
        });
      tail = job.promise;
      return job.promise;
    },
    invalidate() {
      latest = null;
      running = null;
    },
  };
}
